import {
  arrow,
  autoUpdate,
  flip,
  FloatingContext,
  offset as offsetMiddleware,
  Placement,
  ReferenceType,
  shift,
  Strategy,
  useFloating as useFloatingUI,
  useFloatingNodeId,
} from '@floating-ui/react';
import {LegacyRef, MutableRefObject, useRef} from 'react';

import {FloatingPlacement, OptionalCoords} from './FloatingConstants';

const DEFAULT_FLOATING_OFFSET = 0;
const DEFAULT_FLOATING_SHIFT_PADDING = 8;

const FLOATING_PLACEMENT_TO_PLACEMENT: {[key in FloatingPlacement]: Placement} = {
  /** FloatingPlacement.CENTER is a custom implementation. Its initial placement is FloatingPlacement.TOP,
   * which we calculate appropriate offsets and apply in post-processing. The resulting placement is
   * centered horizontally and vertically. * */
  [FloatingPlacement.CENTER]: 'top',
  [FloatingPlacement.TOP]: 'top',
  [FloatingPlacement.TOP_START]: 'top-start',
  [FloatingPlacement.TOP_END]: 'top-end',
  [FloatingPlacement.RIGHT]: 'right',
  [FloatingPlacement.RIGHT_START]: 'right-start',
  [FloatingPlacement.RIGHT_END]: 'right-end',
  [FloatingPlacement.BOTTOM]: 'bottom',
  [FloatingPlacement.BOTTOM_START]: 'bottom-start',
  [FloatingPlacement.BOTTOM_END]: 'bottom-end',
  [FloatingPlacement.LEFT]: 'left',
  [FloatingPlacement.LEFT_START]: 'left-start',
  [FloatingPlacement.LEFT_END]: 'left-end',
};

/** Options for advanced configuration of useFloating positioning behavior, like changing the offset property
 * between the toggle element and the floating element. */
export interface FloatingPlacementOptions {
  /** Determines how many pixels between the toggle element and the floating element. See
   * https://floating-ui.com/docs/offset */
  offsetOverride?: number;
  /** Shift is responsible for repositioning the floating element if it exceeds the visible boundaries in which
   * it is rendered. Shift padding is the amount of space between the boundary and the floating element. See
   * https://floating-ui.com/docs/shift */
  shiftPaddingOverride?: number;
}

interface UsePopoverOptions {
  isOpen: boolean;
  /** @default FloatingPlacement.TOP */
  placement?: FloatingPlacement;
  placementOptions?: FloatingPlacementOptions;
  setOpen: (isOpen: boolean) => void;
}

export interface ArrowProps {
  arrowPos: OptionalCoords;
  arrowRef: MutableRefObject<null>;
  placement: Placement;
}

export interface FloatingProps {
  floatingContext: FloatingContext<ReferenceType>;
  floatingNodeId: string | undefined;
  floatingPos: OptionalCoords;
  floatingRef: LegacyRef<HTMLElement>;
  /** Close the floating element and refocus on the toggle element */
  handleClose: () => void;
}

interface UsePopoverOutput {
  arrowProps: ArrowProps;
  floatingProps: FloatingProps;
  strategy: Strategy;
  toggleRef: LegacyRef<HTMLElement>;
}

/**
 * Calculates the y-offset to translate FloatingPlacement.TOP to FloatingPlacement.CENTER
 */
function getPosYOffsetForPlacementCenter(
  referenceElementHeight?: number,
  floatingElementHeight?: number
): number {
  const referenceHeight = referenceElementHeight ?? 0;
  const floatingHeight = floatingElementHeight ?? 0;
  return (referenceHeight - floatingHeight) / 2 + floatingHeight;
}

/**
 * Hook wrapper around useFloating from @floating-ui which serves as the basis for floating elements like
 * Float and Popover.
 *
 * The base 'useFloating' hook is a general purpose utility which is widely flexible and unopinionated. This
 * version of useFloating injects a number of opinions such as default positioning, resolution strategy, the
 * types of @floating-ui middleware we would like to use, etc.
 *
 * This hook is also serves as convenience wrapper as it makes some choices about type interfaces and how the
 * data is packaged. The information that is returned by default from the base hook is fairly raw.
 */
export default function useFloating({
  isOpen,
  setOpen,
  placement = FloatingPlacement.TOP,
  placementOptions,
}: UsePopoverOptions): UsePopoverOutput {
  const arrowRef = useRef(null);
  const floatingNodeId = useFloatingNodeId();

  const offset = placementOptions?.offsetOverride ?? DEFAULT_FLOATING_OFFSET;
  const shiftPadding = placementOptions?.shiftPaddingOverride ?? DEFAULT_FLOATING_SHIFT_PADDING;

  const {
    x,
    y,
    placement: resolvedPlacement,
    strategy,
    refs,
    context,
    middlewareData,
  } = useFloatingUI({
    nodeId: floatingNodeId,
    open: isOpen,
    onOpenChange: setOpen,
    middleware: [
      offsetMiddleware(offset),
      flip(),
      shift({padding: shiftPadding}),
      arrow({element: arrowRef}),
    ],
    placement: FLOATING_PLACEMENT_TO_PLACEMENT[placement],
    whileElementsMounted: autoUpdate,
  });

  const posYOffset =
    placement === FloatingPlacement.CENTER
      ? getPosYOffsetForPlacementCenter(
          context.refs.reference.current?.getBoundingClientRect().height,
          context.refs.floating.current?.getBoundingClientRect().height
        )
      : 0;
  const floatingPosY = y != null ? y + posYOffset : y;

  const handleClose = (): void => {
    setOpen(false);
    (refs.reference.current as HTMLElement).focus();
  };

  return {
    arrowProps: {
      arrowPos: {x: middlewareData.arrow?.x ?? null, y: middlewareData.arrow?.y ?? null},
      arrowRef,
      placement: resolvedPlacement,
    },
    floatingProps: {
      floatingContext: context,
      floatingNodeId,
      floatingPos: {x, y: floatingPosY},
      floatingRef: refs.setFloating,
      handleClose,
    },
    strategy,
    toggleRef: refs.setReference,
  };
}
